import {
  Badge,
  Box,
  Button,
  Card,
  Flex,
  Heading,
  HStack,
  Input,
  Spinner,
  Table,
  Text,
  Textarea,
  VStack,
} from '@chakra-ui/react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  CodeIcon,
  EyeIcon,
  PlusIcon,
  RefreshCwIcon,
  RocketIcon,
  RotateCcwIcon,
  ScaleIcon,
  SearchIcon,
  Trash2Icon,
  XIcon,
} from 'lucide-react'
import { useState } from 'react'
import {
  k8sApi,
  type Deployment,
  type CreateDeploymentRequest,
  type CreateContainerSpec,
} from '~/lib/api'
import { toaster } from '~/toaster'

interface DeploymentsTabProps {
  namespace: string
  isAdmin: boolean
}

const PAGE_SIZE = 10

// View Deployment Modal
function ViewDeploymentModal({
  deployment,
  onClose,
}: {
  deployment: Deployment
  onClose: () => void
}) {
  return (
    <Box position="fixed" inset={0} bg="blackAlpha.600" zIndex={1000} onClick={onClose}>
      <Box
        position="fixed"
        top="50%"
        left="50%"
        transform="translate(-50%, -50%)"
        bg="bg.panel"
        borderRadius="lg"
        w="90vw"
        maxW="700px"
        maxH="80vh"
        overflow="hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <Flex
          justify="space-between"
          align="center"
          p={4}
          borderBottomWidth="1px"
          borderColor="border"
        >
          <VStack align="flex-start" gap={0}>
            <Heading size="md">Deployment Details</Heading>
            <Text fontSize="sm" color="content.secondary">
              {deployment.namespace}/{deployment.name}
            </Text>
          </VStack>
          <Button
            size="sm"
            variant="ghost"
            onClick={onClose}
            aria-label="Close dialog"
            minW="44px"
            minH="44px"
          >
            <XIcon size={16} />
          </Button>
        </Flex>
        <Box overflow="auto" maxH="calc(80vh - 80px)" p={4}>
          <VStack align="stretch" gap={4}>
            <Box>
              <Text fontWeight="semibold" mb={2}>
                Replicas
              </Text>
              <HStack gap={4}>
                <Box>
                  <Text fontSize="xs" color="content.secondary">
                    Desired
                  </Text>
                  <Badge colorPalette="blue" fontSize="md">
                    {deployment.replicas}
                  </Badge>
                </Box>
                <Box>
                  <Text fontSize="xs" color="content.secondary">
                    Ready
                  </Text>
                  <Badge colorPalette="green" fontSize="md">
                    {deployment.readyReplicas}
                  </Badge>
                </Box>
                <Box>
                  <Text fontSize="xs" color="content.secondary">
                    Available
                  </Text>
                  <Badge colorPalette="green" fontSize="md">
                    {deployment.availableReplicas}
                  </Badge>
                </Box>
                <Box>
                  <Text fontSize="xs" color="content.secondary">
                    Updated
                  </Text>
                  <Badge colorPalette="blue" fontSize="md">
                    {deployment.updatedReplicas}
                  </Badge>
                </Box>
              </HStack>
            </Box>

            <Box>
              <Text fontWeight="semibold" mb={2}>
                Metadata
              </Text>
              <HStack gap={4} flexWrap="wrap">
                <Box>
                  <Text fontSize="xs" color="content.secondary">
                    Strategy
                  </Text>
                  <Badge colorPalette="purple">{deployment.strategy}</Badge>
                </Box>
                <Box>
                  <Text fontSize="xs" color="content.secondary">
                    Age
                  </Text>
                  <Text>{deployment.age}</Text>
                </Box>
              </HStack>
            </Box>

            {deployment.labels && Object.keys(deployment.labels).length > 0 && (
              <Box>
                <Text fontWeight="semibold" mb={2}>
                  Labels
                </Text>
                <HStack gap={2} flexWrap="wrap">
                  {Object.entries(deployment.labels).map(([k, v]) => (
                    <Badge key={k} variant="outline" fontFamily="mono">
                      {k}={v}
                    </Badge>
                  ))}
                </HStack>
              </Box>
            )}

            {deployment.selector && Object.keys(deployment.selector).length > 0 && (
              <Box>
                <Text fontWeight="semibold" mb={2}>
                  Selector
                </Text>
                <HStack gap={2} flexWrap="wrap">
                  {Object.entries(deployment.selector).map(([k, v]) => (
                    <Badge key={k} variant="subtle" colorPalette="cyan" fontFamily="mono">
                      {k}={v}
                    </Badge>
                  ))}
                </HStack>
              </Box>
            )}
          </VStack>
        </Box>
      </Box>
    </Box>
  )
}

// Scale Dialog
function ScaleDialog({
  deployment,
  onClose,
  onSuccess,
}: {
  deployment: Deployment
  onClose: () => void
  onSuccess: () => void
}) {
  const [replicas, setReplicas] = useState(deployment.replicas)

  const scaleMutation = useMutation({
    mutationFn: () => k8sApi.scaleDeployment(deployment.namespace, deployment.name, replicas),
    onSuccess: () => {
      toaster.create({
        title: 'Deployment scaled',
        description: `Scaled to ${replicas} replicas`,
        type: 'success',
      })
      onSuccess()
      onClose()
    },
    onError: (error: Error) => {
      toaster.create({
        title: 'Failed to scale deployment',
        description: error.message,
        type: 'error',
      })
    },
  })

  return (
    <Box position="fixed" inset={0} bg="blackAlpha.600" zIndex={1000} onClick={onClose}>
      <Box
        position="fixed"
        top="50%"
        left="50%"
        transform="translate(-50%, -50%)"
        bg="bg.panel"
        borderRadius="lg"
        w="400px"
        onClick={(e) => e.stopPropagation()}
      >
        <Flex
          justify="space-between"
          align="center"
          p={4}
          borderBottomWidth="1px"
          borderColor="border"
        >
          <Heading size="md">Scale Deployment</Heading>
          <Button
            size="sm"
            variant="ghost"
            onClick={onClose}
            aria-label="Close dialog"
            minW="44px"
            minH="44px"
          >
            <XIcon size={16} />
          </Button>
        </Flex>
        <Box p={4}>
          <VStack align="stretch" gap={4}>
            <Text fontSize="sm" color="content.secondary">
              {deployment.namespace}/{deployment.name}
            </Text>
            <Box>
              <Text fontWeight="semibold" mb={2}>
                Replicas
              </Text>
              <HStack>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setReplicas(Math.max(0, replicas - 1))}
                  disabled={replicas <= 0}
                >
                  -
                </Button>
                <Input
                  type="number"
                  min={0}
                  value={replicas}
                  onChange={(e) => setReplicas(parseInt(e.target.value) || 0)}
                  textAlign="center"
                  w="80px"
                />
                <Button size="sm" variant="outline" onClick={() => setReplicas(replicas + 1)}>
                  +
                </Button>
              </HStack>
              <Text fontSize="xs" color="content.secondary" mt={1}>
                Current: {deployment.replicas} | Ready: {deployment.readyReplicas}
              </Text>
            </Box>
          </VStack>
        </Box>
        <Flex justify="flex-end" gap={2} p={4} borderTopWidth="1px" borderColor="border">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            colorPalette="blue"
            onClick={() => scaleMutation.mutate()}
            loading={scaleMutation.isPending}
          >
            Scale
          </Button>
        </Flex>
      </Box>
    </Box>
  )
}

// Manifest Modal
function ManifestModal({ deployment, onClose }: { deployment: Deployment; onClose: () => void }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['deployment-manifest', deployment.namespace, deployment.name],
    queryFn: () => k8sApi.deploymentManifest(deployment.namespace, deployment.name),
  })

  return (
    <Box position="fixed" inset={0} bg="blackAlpha.600" zIndex={1000} onClick={onClose}>
      <Box
        position="fixed"
        top="50%"
        left="50%"
        transform="translate(-50%, -50%)"
        bg="bg.panel"
        borderRadius="lg"
        w="90vw"
        maxW="900px"
        maxH="85vh"
        overflow="hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <Flex
          justify="space-between"
          align="center"
          p={4}
          borderBottomWidth="1px"
          borderColor="border"
        >
          <VStack align="flex-start" gap={0}>
            <Heading size="md">Deployment Manifest</Heading>
            <Text fontSize="sm" color="content.secondary">
              {deployment.namespace}/{deployment.name}
            </Text>
          </VStack>
          <HStack gap={2}>
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                if (data?.manifest) {
                  navigator.clipboard.writeText(data.manifest)
                  toaster.create({
                    title: 'Copied to clipboard',
                    type: 'success',
                  })
                }
              }}
              disabled={!data?.manifest}
            >
              Copy
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={onClose}
              aria-label="Close dialog"
              minW="44px"
              minH="44px"
            >
              <XIcon size={16} />
            </Button>
          </HStack>
        </Flex>
        <Box overflow="auto" maxH="calc(85vh - 80px)" p={4}>
          {isLoading ? (
            <Flex justify="center" py={8}>
              <Spinner />
            </Flex>
          ) : error ? (
            <Text color="danger.fg">Failed to load manifest</Text>
          ) : (
            <Box
              as="pre"
              fontFamily="mono"
              fontSize="sm"
              p={4}
              bg="bg.subtle"
              borderRadius="md"
              overflow="auto"
              whiteSpace="pre-wrap"
            >
              {data?.manifest}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  )
}

// Create Deployment Modal
function CreateDeploymentModal({
  namespace,
  onClose,
  onSuccess,
}: {
  namespace: string
  onClose: () => void
  onSuccess: () => void
}) {
  const [name, setName] = useState('')
  const [replicas, setReplicas] = useState(1)
  const [selectorKey, setSelectorKey] = useState('app')
  const [selectorValue, setSelectorValue] = useState('')
  const [containerName, setContainerName] = useState('')
  const [containerImage, setContainerImage] = useState('')
  const [containerPort, setContainerPort] = useState('')

  const createMutation = useMutation({
    mutationFn: (request: CreateDeploymentRequest) => k8sApi.createDeployment(namespace, request),
    onSuccess: () => {
      toaster.create({
        title: 'Deployment created',
        description: `${name} created successfully`,
        type: 'success',
      })
      onSuccess()
      onClose()
    },
    onError: (error: Error) => {
      toaster.create({
        title: 'Failed to create deployment',
        description: error.message,
        type: 'error',
      })
    },
  })

  const handleSubmit = () => {
    if (!name || !selectorValue || !containerName || !containerImage) {
      toaster.create({
        title: 'Validation error',
        description: 'Please fill in all required fields',
        type: 'error',
      })
      return
    }

    const container: CreateContainerSpec = {
      name: containerName,
      image: containerImage,
    }

    if (containerPort) {
      container.ports = [
        {
          containerPort: parseInt(containerPort, 10),
          protocol: 'TCP',
        },
      ]
    }

    const request: CreateDeploymentRequest = {
      name,
      replicas,
      selector: { [selectorKey]: selectorValue },
      containers: [container],
    }

    createMutation.mutate(request)
  }

  return (
    <Box position="fixed" inset={0} bg="blackAlpha.600" zIndex={1000} onClick={onClose}>
      <Box
        position="fixed"
        top="50%"
        left="50%"
        transform="translate(-50%, -50%)"
        bg="bg.panel"
        borderRadius="lg"
        w="500px"
        maxH="85vh"
        overflow="hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <Flex
          justify="space-between"
          align="center"
          p={4}
          borderBottomWidth="1px"
          borderColor="border"
        >
          <Heading size="md">Create Deployment</Heading>
          <Button
            size="sm"
            variant="ghost"
            onClick={onClose}
            aria-label="Close dialog"
            minW="44px"
            minH="44px"
          >
            <XIcon size={16} />
          </Button>
        </Flex>
        <Box overflow="auto" maxH="calc(85vh - 140px)" p={4}>
          <VStack align="stretch" gap={4}>
            <Box>
              <Text fontWeight="semibold" mb={2}>
                Namespace
              </Text>
              <Badge colorPalette="blue">{namespace}</Badge>
            </Box>

            <Box>
              <Text fontWeight="semibold" mb={2}>
                Name *
              </Text>
              <Input
                placeholder="my-deployment"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </Box>

            <Box>
              <Text fontWeight="semibold" mb={2}>
                Replicas
              </Text>
              <HStack>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setReplicas(Math.max(0, replicas - 1))}
                  disabled={replicas <= 0}
                >
                  -
                </Button>
                <Input
                  type="number"
                  min={0}
                  value={replicas}
                  onChange={(e) => setReplicas(parseInt(e.target.value) || 0)}
                  textAlign="center"
                  w="80px"
                />
                <Button size="sm" variant="outline" onClick={() => setReplicas(replicas + 1)}>
                  +
                </Button>
              </HStack>
            </Box>

            <Box>
              <Text fontWeight="semibold" mb={2}>
                Selector *
              </Text>
              <HStack>
                <Input
                  placeholder="app"
                  value={selectorKey}
                  onChange={(e) => setSelectorKey(e.target.value)}
                  w="120px"
                />
                <Text>=</Text>
                <Input
                  placeholder="my-app"
                  value={selectorValue}
                  onChange={(e) => setSelectorValue(e.target.value)}
                  flex={1}
                />
              </HStack>
            </Box>

            <Box borderWidth="1px" borderRadius="md" p={3}>
              <Text fontWeight="semibold" mb={3}>
                Container
              </Text>
              <VStack align="stretch" gap={3}>
                <Box>
                  <Text fontSize="sm" mb={1}>
                    Container Name *
                  </Text>
                  <Input
                    placeholder="container-name"
                    value={containerName}
                    onChange={(e) => setContainerName(e.target.value)}
                    size="sm"
                  />
                </Box>
                <Box>
                  <Text fontSize="sm" mb={1}>
                    Image *
                  </Text>
                  <Input
                    placeholder="nginx:latest"
                    value={containerImage}
                    onChange={(e) => setContainerImage(e.target.value)}
                    size="sm"
                  />
                </Box>
                <Box>
                  <Text fontSize="sm" mb={1}>
                    Port (optional)
                  </Text>
                  <Input
                    type="number"
                    placeholder="80"
                    value={containerPort}
                    onChange={(e) => setContainerPort(e.target.value)}
                    size="sm"
                    w="120px"
                  />
                </Box>
              </VStack>
            </Box>
          </VStack>
        </Box>
        <Flex justify="flex-end" gap={2} p={4} borderTopWidth="1px" borderColor="border">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button colorPalette="green" onClick={handleSubmit} loading={createMutation.isPending}>
            Create Deployment
          </Button>
        </Flex>
      </Box>
    </Box>
  )
}

export function DeploymentsTab({ namespace, isAdmin }: DeploymentsTabProps) {
  const queryClient = useQueryClient()
  const [currentPage, setCurrentPage] = useState(1)
  const [searchQuery, setSearchQuery] = useState('')
  const [viewDeployment, setViewDeployment] = useState<Deployment | null>(null)
  const [scaleDeployment, setScaleDeployment] = useState<Deployment | null>(null)
  const [manifestDeployment, setManifestDeployment] = useState<Deployment | null>(null)
  const [showCreateModal, setShowCreateModal] = useState(false)

  // Fetch deployments
  const {
    data: deploymentsData,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ['deployments', namespace],
    queryFn: () => k8sApi.deployments(namespace || undefined),
    retry: false,
  })

  // Restart mutation
  const restartMutation = useMutation({
    mutationFn: ({ namespace, name }: { namespace: string; name: string }) =>
      k8sApi.restartDeployment(namespace, name),
    onSuccess: (_, variables) => {
      toaster.create({
        title: 'Deployment restart initiated',
        description: `${variables.name} will restart`,
        type: 'success',
      })
      queryClient.invalidateQueries({ queryKey: ['deployments'] })
    },
    onError: (error: Error) => {
      toaster.create({
        title: 'Failed to restart deployment',
        description: error.message,
        type: 'error',
      })
    },
  })

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: ({ namespace, name }: { namespace: string; name: string }) =>
      k8sApi.deleteDeployment(namespace, name),
    onSuccess: (_, variables) => {
      toaster.create({
        title: 'Deployment deleted',
        description: `${variables.name} deleted successfully`,
        type: 'success',
      })
      queryClient.invalidateQueries({ queryKey: ['deployments'] })
    },
    onError: (error: Error) => {
      toaster.create({
        title: 'Failed to delete deployment',
        description: error.message,
        type: 'error',
      })
    },
  })

  const handleRestart = (namespace: string, name: string) => {
    if (window.confirm(`Are you sure you want to restart deployment "${name}"?`)) {
      restartMutation.mutate({ namespace, name })
    }
  }

  const handleDelete = (namespace: string, name: string) => {
    if (
      window.confirm(
        `Are you sure you want to delete deployment "${name}"?\n\nThis will terminate all pods managed by this deployment.`,
      )
    ) {
      deleteMutation.mutate({ namespace, name })
    }
  }

  const allDeployments = deploymentsData?.deployments || []

  // Filter based on search query
  const filteredDeployments = allDeployments.filter((d) => {
    if (!searchQuery.trim()) return true
    const query = searchQuery.toLowerCase()
    return (
      d.name.toLowerCase().includes(query) ||
      d.namespace.toLowerCase().includes(query) ||
      d.strategy.toLowerCase().includes(query)
    )
  })

  // Pagination
  const totalPages = Math.ceil(filteredDeployments.length / PAGE_SIZE)
  const startIndex = (currentPage - 1) * PAGE_SIZE
  const endIndex = startIndex + PAGE_SIZE
  const deployments = filteredDeployments.slice(startIndex, endIndex)

  const handleSearchChange = (query: string) => {
    setSearchQuery(query)
    setCurrentPage(1)
  }

  const clearSearch = () => {
    setSearchQuery('')
    setCurrentPage(1)
  }

  const handleSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['deployments'] })
  }

  return (
    <VStack align="stretch" gap={4}>
      <Flex justify="space-between" align="center">
        <Box position="relative" w="300px">
          <HStack
            position="absolute"
            left={3}
            top="50%"
            transform="translateY(-50%)"
            zIndex={1}
            pointerEvents="none"
          >
            <SearchIcon size={16} color="gray" />
          </HStack>
          <Input
            size="sm"
            pl={10}
            pr={searchQuery ? 10 : 3}
            placeholder="Search by name, namespace, strategy..."
            value={searchQuery}
            onChange={(e) => handleSearchChange(e.target.value)}
          />
          {searchQuery && (
            <Button
              size="xs"
              variant="ghost"
              position="absolute"
              right={1}
              top="50%"
              transform="translateY(-50%)"
              onClick={clearSearch}
              aria-label="Clear search"
              minW="auto"
              h="auto"
              p={1}
            >
              <XIcon size={14} />
            </Button>
          )}
        </Box>
        <HStack gap={2}>
          {isAdmin && namespace && (
            <Button size="sm" colorPalette="green" onClick={() => setShowCreateModal(true)}>
              <PlusIcon size={16} />
              Create Deployment
            </Button>
          )}
          <Button size="sm" onClick={() => refetch()}>
            <RefreshCwIcon size={16} />
            Refresh
          </Button>
        </HStack>
      </Flex>

      <Card.Root>
        <Table.Root>
          <Table.Header>
            <Table.Row bg="bg.subtle">
              <Table.ColumnHeader>Name</Table.ColumnHeader>
              <Table.ColumnHeader>Namespace</Table.ColumnHeader>
              <Table.ColumnHeader>Ready</Table.ColumnHeader>
              <Table.ColumnHeader>Strategy</Table.ColumnHeader>
              <Table.ColumnHeader>Age</Table.ColumnHeader>
              <Table.ColumnHeader>Actions</Table.ColumnHeader>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {isLoading ? (
              <Table.Row>
                <Table.Cell colSpan={6}>
                  <Flex justify="center" py={8}>
                    <Spinner />
                  </Flex>
                </Table.Cell>
              </Table.Row>
            ) : deployments.length === 0 ? (
              <Table.Row>
                <Table.Cell colSpan={6}>
                  <VStack py={8} gap={2}>
                    <RocketIcon size={32} color="gray" />
                    <Text textAlign="center" color="content.secondary">
                      {searchQuery
                        ? `No Deployments matching "${searchQuery}"`
                        : namespace
                          ? 'No Deployments in this namespace'
                          : 'No Deployments found'}
                    </Text>
                    {searchQuery && (
                      <Button size="sm" variant="ghost" onClick={clearSearch}>
                        Clear search
                      </Button>
                    )}
                  </VStack>
                </Table.Cell>
              </Table.Row>
            ) : (
              deployments.map((d) => {
                const isHealthy = d.readyReplicas === d.replicas && d.replicas > 0
                const isScaling = d.readyReplicas !== d.replicas
                return (
                  <Table.Row key={`${d.namespace}/${d.name}`}>
                    <Table.Cell>
                      <Text fontWeight="medium">{d.name}</Text>
                    </Table.Cell>
                    <Table.Cell>
                      <Badge variant="outline">{d.namespace}</Badge>
                    </Table.Cell>
                    <Table.Cell>
                      <Badge colorPalette={isHealthy ? 'green' : isScaling ? 'yellow' : 'red'}>
                        {d.readyReplicas}/{d.replicas}
                      </Badge>
                    </Table.Cell>
                    <Table.Cell>
                      <Badge colorPalette="purple" variant="subtle">
                        {d.strategy}
                      </Badge>
                    </Table.Cell>
                    <Table.Cell>{d.age}</Table.Cell>
                    <Table.Cell>
                      <HStack gap={2}>
                        <Button size="xs" variant="ghost" onClick={() => setViewDeployment(d)}>
                          <EyeIcon size={14} />
                          View
                        </Button>
                        <Button
                          size="xs"
                          variant="ghost"
                          colorPalette="purple"
                          onClick={() => setManifestDeployment(d)}
                        >
                          <CodeIcon size={14} />
                          YAML
                        </Button>
                        {isAdmin && (
                          <>
                            <Button
                              size="xs"
                              variant="ghost"
                              colorPalette="blue"
                              onClick={() => setScaleDeployment(d)}
                            >
                              <ScaleIcon size={14} />
                              Scale
                            </Button>
                            <Button
                              size="xs"
                              variant="ghost"
                              colorPalette="orange"
                              loading={restartMutation.isPending}
                              onClick={() => handleRestart(d.namespace, d.name)}
                            >
                              <RotateCcwIcon size={14} />
                              Restart
                            </Button>
                            <Button
                              size="xs"
                              variant="ghost"
                              colorPalette="red"
                              loading={deleteMutation.isPending}
                              onClick={() => handleDelete(d.namespace, d.name)}
                            >
                              <Trash2Icon size={14} />
                              Delete
                            </Button>
                          </>
                        )}
                      </HStack>
                    </Table.Cell>
                  </Table.Row>
                )
              })
            )}
          </Table.Body>
        </Table.Root>
      </Card.Root>

      {/* Pagination */}
      {totalPages > 1 && (
        <Flex justify="space-between" align="center" pt={2}>
          <Text fontSize="sm" color="content.secondary">
            Showing {startIndex + 1}-{Math.min(endIndex, filteredDeployments.length)} of{' '}
            {filteredDeployments.length} Deployments
            {searchQuery && ` (filtered from ${allDeployments.length})`}
          </Text>
          <HStack gap={2}>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
            >
              <ChevronLeftIcon size={16} />
              Previous
            </Button>
            <HStack gap={1}>
              {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                let page: number
                if (totalPages <= 5) {
                  page = i + 1
                } else if (currentPage <= 3) {
                  page = i + 1
                } else if (currentPage >= totalPages - 2) {
                  page = totalPages - 4 + i
                } else {
                  page = currentPage - 2 + i
                }
                return (
                  <Button
                    key={page}
                    size="sm"
                    variant={page === currentPage ? 'solid' : 'outline'}
                    colorPalette={page === currentPage ? 'blue' : undefined}
                    onClick={() => setCurrentPage(page)}
                    minW="40px"
                  >
                    {page}
                  </Button>
                )
              })}
            </HStack>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
            >
              Next
              <ChevronRightIcon size={16} />
            </Button>
          </HStack>
        </Flex>
      )}

      {/* View Modal */}
      {viewDeployment && (
        <ViewDeploymentModal deployment={viewDeployment} onClose={() => setViewDeployment(null)} />
      )}

      {/* Scale Dialog */}
      {scaleDeployment && (
        <ScaleDialog
          deployment={scaleDeployment}
          onClose={() => setScaleDeployment(null)}
          onSuccess={handleSuccess}
        />
      )}

      {/* Manifest Modal */}
      {manifestDeployment && (
        <ManifestModal
          deployment={manifestDeployment}
          onClose={() => setManifestDeployment(null)}
        />
      )}

      {/* Create Deployment Modal */}
      {showCreateModal && namespace && (
        <CreateDeploymentModal
          namespace={namespace}
          onClose={() => setShowCreateModal(false)}
          onSuccess={handleSuccess}
        />
      )}
    </VStack>
  )
}
